/**
 * Optional data sources for a <sessionboard-embed> beyond `embed-id`.
 *
 * - `data-url="https://…/program/speakers"`: fetch the async-data envelope
 *   from this URL instead of `/embed/v2/{embedId}/async-data`. The usual
 *   `dataType`/`tz`/`language`/`ran` params are appended and 202 polling is
 *   unchanged. Used by Sessionboard Sites (SB-9253), which serve the same
 *   payload for a Site page that has no embed row.
 * - `data-inject-payload`: never fetch. The host page supplies the payload by
 *   assigning `element.payload = {...}` or dispatching a
 *   `sb-embed-payload` CustomEvent (payload in `detail`) on the element, as
 *   often as it likes; the widget re-renders on each. Used by the Site
 *   builder's live preview (SB-9250) to render unsaved settings.
 */

export const PAYLOAD_EVENT = 'sb-embed-payload';

/**
 * Dispatched on the element when a fetch fails for good (after any 202
 * polling). `detail` = `{ status, code, message }`; status/code are null for
 * network failures. Lets a host fall back, e.g. a Site on 404 NO_VIEW_CONFIG.
 */
export const ERROR_EVENT = 'sb-embed-error';

/**
 * @param {HTMLElement} element
 * @returns {{ dataUrl: string | null, injectPayload: boolean }}
 */
export const readDataSourceAttributes = (element) => ({
  dataUrl: element.getAttribute('data-url') || null,
  injectPayload: element.hasAttribute('data-inject-payload'),
});

/**
 * Adds a `payload` property to a custom element class. Setting it stores the
 * value (so a widget mounted later still reads it) and notifies a mounted
 * widget through {@link PAYLOAD_EVENT}.
 *
 * @param {typeof HTMLElement} ElementClass
 */
export const definePayloadProperty = (ElementClass) => {
  if (Object.prototype.hasOwnProperty.call(ElementClass.prototype, 'payload')) return;
  Object.defineProperty(ElementClass.prototype, 'payload', {
    configurable: true,
    get() {
      return this._sbPayload;
    },
    set(value) {
      this._sbPayload = value;
      this.dispatchEvent(new CustomEvent(PAYLOAD_EVENT, { detail: value }));
    },
  });
};

/**
 * Builds the async-data request URL. With no `dataUrl` the string is exactly
 * the pre-SB-9253 embed URL.
 *
 * @param {Object} params
 * @param {string} params.apiUrl
 * @param {string | null | undefined} params.embedId
 * @param {string | null | undefined} params.dataUrl
 * @param {string} params.widgetType
 * @param {string} params.clientTimezone
 * @param {string | null | undefined} params.language
 * @param {number} params.ran cache-buster
 * @returns {string}
 */
export const buildAsyncDataUrl = ({ apiUrl, embedId, dataUrl, widgetType, clientTimezone, language, ran }) => {
  const languageParam = language ? `&language=${encodeURIComponent(language)}` : '';
  const query = `dataType=${widgetType}&tz=${clientTimezone}${languageParam}&ran=${ran}`;
  if (dataUrl) {
    const separator = dataUrl.includes('?') ? '&' : '?';
    return `${dataUrl}${separator}${query}`;
  }
  return `${apiUrl}/embed/v2/${embedId}/async-data?${query}`;
};

/**
 * CSS-safe font stack from a payload `fontFamily`, or null when absent or
 * unsafe to interpolate into a stylesheet. A bare family name gets quoted and
 * a generic fallback.
 *
 * @param {unknown} value
 * @returns {string | null}
 */
export const toFontStack = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 200 || /[;{}<>\\@]/.test(trimmed)) return null;
  if (trimmed.includes(',')) return trimmed;
  const unquoted = trimmed.replace(/^['"]|['"]$/g, '');
  if (!unquoted || /['"]/.test(unquoted)) return null;
  return `'${unquoted}', sans-serif`;
};
